import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Archive,
  BriefcaseBusiness,
  Download,
  Eye,
  FileText,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  UserCheck,
  Users,
  X
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../utils/auth';
import { CLIENT_MANAGEMENT_PRIVILEGES } from '../../../utils/clientManagementPrivileges';
import api from '../../../utils/api';
import Sidebar from '../../../components/Sidebar/sidebar';
import Header from '../../../components/Header';
import './RetailClientData.css';
import { API_BASE_URL } from '../../../utils/apiBase';
import { confirmDialog } from '../../../components/notifications/notificationService';

const RetailClientList = () => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('edit');
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [confirmationId, setConfirmationId] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    owner: '',
    assignedUser: '',
    city: '',
    state: '',
    status: ''
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [quickViewClient, setQuickViewClient] = useState(null);
  const [documentClient, setDocumentClient] = useState(null);
  const initialClientState = useRef(null);
  const privileges = getUserPrivileges();
  const role = getUserRole();
  const retailPrivileges = CLIENT_MANAGEMENT_PRIVILEGES.retail;
  const hasRetailPrivilege = (key) => role === 'admin' || privileges.includes(key);
  const canExportRetailClients = hasRetailPrivilege(retailPrivileges.export);
  const canAddRetailClients = hasRetailPrivilege(retailPrivileges.add);
  const canViewRetailClients = hasRetailPrivilege(retailPrivileges.view);
  const canDeleteRetailClients =
    hasRetailPrivilege(retailPrivileges.delete);
  const canEditRetailClients =
    hasRetailPrivilege(retailPrivileges.edit);
  const canListRetailClients =
    canViewRetailClients || canExportRetailClients || canEditRetailClients || canDeleteRetailClients;
  const canShowRetailActions = canViewRetailClients || canEditRetailClients || canDeleteRetailClients;
  const canReassignRetailOwner = role === 'admin';

  const formatAuditTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-IN');
  };

  const getOwnerValue = (owner) => {
    if (!owner) return '';
    return typeof owner === 'object' ? owner._id || '' : owner;
  };

  const getOwnerLabel = useCallback((client) =>
    client?.ownerId?.name || client?.ownerId?.username || client?.ownerName || '-',
  []);

  const getAssignedUserLabel = useCallback((client) =>
    client?.assignedUser?.name ||
    client?.assignedUser?.username ||
    client?.assignedTo?.name ||
    client?.assignedTo?.username ||
    client?.assignedUserName ||
    getOwnerLabel(client),
  [getOwnerLabel]);

  const getClientStatus = (client) => {
    const explicitStatus = client?.status || client?.clientStatus || client?.activeStatus;
    if (explicitStatus) return String(explicitStatus);
    return client?.isActive === false ? 'Inactive' : 'Active';
  };

  const getClientFiles = (client) => {
    const files = [];
    if (client?.aadharFile) files.push({ label: 'Aadhar', path: client.aadharFile });
    if (client?.panFile) files.push({ label: 'PAN', path: client.panFile });
    return files;
  };

  const getInitials = (name = '') => {
    const words = String(name).trim().split(/\s+/).filter(Boolean);
    if (!words.length) return 'RC';
    return words.slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  };

  const isAddedThisMonth = (client) => {
    const createdDate = new Date(client?.createdAt || client?.createdDate || client?.updatedAt);
    if (Number.isNaN(createdDate.getTime())) return false;
    const now = new Date();
    return createdDate.getMonth() === now.getMonth() && createdDate.getFullYear() === now.getFullYear();
  };

  const hasClientPolicy = (client) =>
    Boolean(client?.policyNumber || client?.policyId || client?.policies?.length || client?.convertedPolicyId);

  const uniqueOptions = (values) =>
    [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    );

  const summary = useMemo(() => {
    const activeClients = clients.filter((client) => getClientStatus(client).toLowerCase() === 'active').length;
    return {
      total: clients.length,
      active: activeClients,
      addedThisMonth: clients.filter(isAddedThisMonth).length,
      assigned: clients.filter((client) => getOwnerLabel(client) !== '-' || getAssignedUserLabel(client) !== '-').length,
      withPolicies: clients.filter(hasClientPolicy).length
    };
  }, [clients, getAssignedUserLabel, getOwnerLabel]);

  const filterOptions = useMemo(
    () => ({
      owners: uniqueOptions(clients.map(getOwnerLabel).filter((value) => value !== '-')),
      assignedUsers: uniqueOptions(clients.map(getAssignedUserLabel).filter((value) => value !== '-')),
      cities: uniqueOptions(clients.map((client) => client.city)),
      states: uniqueOptions(clients.map((client) => client.state)),
      statuses: uniqueOptions(clients.map(getClientStatus))
    }),
    [clients, getAssignedUserLabel, getOwnerLabel]
  );

  const filteredClients = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    return clients.filter((client) => {
      const searchText = [
        client.autoGeneratedId,
        client.clientName,
        client.phoneNumber,
        client.emailId
      ].join(' ').toLowerCase();

      const matchesSearch = !query || searchText.includes(query);
      const matchesOwner = !appliedFilters.owner || getOwnerLabel(client) === appliedFilters.owner;
      const matchesAssigned =
        !appliedFilters.assignedUser || getAssignedUserLabel(client) === appliedFilters.assignedUser;
      const matchesCity = !appliedFilters.city || client.city === appliedFilters.city;
      const matchesState = !appliedFilters.state || client.state === appliedFilters.state;
      const matchesStatus = !appliedFilters.status || getClientStatus(client) === appliedFilters.status;

      return matchesSearch && matchesOwner && matchesAssigned && matchesCity && matchesState && matchesStatus;
    });
  }, [clients, appliedFilters, getAssignedUserLabel, getOwnerLabel]);

  const selectedClients = useMemo(
    () => clients.filter((client) => selectedClientIds.includes(client.autoGeneratedId)),
    [clients, selectedClientIds]
  );

  const summaryCards = [
    { label: 'Total Retail Clients', value: summary.total, note: 'All client records', icon: Users, tone: 'is-blue' },
    { label: 'Active Clients', value: summary.active, note: 'Currently active', icon: ShieldCheck, tone: 'is-green' },
    { label: 'Clients Added This Month', value: summary.addedThisMonth, note: 'New this month', icon: UserCheck, tone: 'is-amber' },
    { label: 'Assigned Clients', value: summary.assigned, note: 'Owner mapped', icon: BriefcaseBusiness, tone: 'is-teal' },
    { label: 'Clients With Policies', value: summary.withPolicies, note: 'Policy linked', icon: Archive, tone: 'is-indigo' }
  ];

  // Deep comparison function for arrays of objects (like familyMembers, children)
  const areArraysEqual = (arr1, arr2) => {
    if (!arr1 || !arr2) return arr1 === arr2;
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
      const obj1 = arr1[i];
      const obj2 = arr2[i];

      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      for (const key of keys1) {
        if (obj1[key] !== obj2[key]) {
          return false;
        }
      }
    }
    return true;
  };

  // Fetch client data from the backend
  useEffect(() => {
    if (!canListRetailClients) {
      setLoading(false);
      return;
    }

    const fetchClients = async () => {
      try {
        const response = await api.get(`${API_BASE_URL}/api/client/all`);
        setClients(response.data);
      } catch (error) {
        console.error('Error fetching client list:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchClients();
  }, [canListRetailClients]);

  useEffect(() => {
    if (!canReassignRetailOwner) return;

    const fetchEmployees = async () => {
      try {
        const response = await api.get(`${API_BASE_URL}/api/auth/employee-options`);
        setEmployeeOptions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching employee options:', error);
      }
    };

    fetchEmployees();
  }, [canReassignRetailOwner]);

  const openClientModal = (client, mode = 'edit') => {
    const clientWithDefaultArrays = {
      ...client,
      familyMembers: client.familyMembers || [],
      children: client.children || [],
      ownerHistory: client.ownerHistory || []
    };
    setSelectedClient(clientWithDefaultArrays);
    initialClientState.current = JSON.parse(JSON.stringify(clientWithDefaultArrays));
    setModalMode(mode);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedClient(null);
    initialClientState.current = null;
    setModalMode('edit');
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setSelectedClientIds([]);
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      search: '',
      owner: '',
      assignedUser: '',
      city: '',
      state: '',
      status: ''
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSelectedClientIds([]);
  };

  const exportClients = (rows = filteredClients, filename = 'retail-clients.xlsx') => {
    const exportRows = rows.map((client) => ({
      'Client ID': client.autoGeneratedId || '',
      'Client Name': client.clientName || '',
      Owner: getOwnerLabel(client),
      'Assigned User': getAssignedUserLabel(client),
      City: client.city || '',
      State: client.state || '',
      Status: getClientStatus(client),
      Phone: client.phoneNumber || '',
      Email: client.emailId || '',
      Address: client.prospectAddress || '',
      Files: getClientFiles(client).length
    }));
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Retail Clients');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), filename);
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId]
    );
  };

  const toggleAllFilteredClients = (checked) => {
    setSelectedClientIds(checked ? filteredClients.map((client) => client.autoGeneratedId) : []);
  };

  const handleBulkDelete = async () => {
    if (!canDeleteRetailClients || selectedClientIds.length === 0) return;
    const confirmed = await confirmDialog({ title: 'Delete selected clients?', message: `${selectedClientIds.length} retail client record(s) will be permanently removed.`, confirmText: 'Delete Clients' });
    if (!confirmed) return;

    try {
      await Promise.all(
        selectedClientIds.map((clientId) => api.delete(`${API_BASE_URL}/api/client/${clientId}`))
      );
      setClients((prev) => prev.filter((client) => !selectedClientIds.includes(client.autoGeneratedId)));
      setSelectedClientIds([]);
      alert('Selected clients deleted successfully.');
    } catch (error) {
      console.error('Error deleting selected clients:', error);
      alert('Unable to delete all selected clients.');
    }
  };

  const handleBulkAssignOwner = () => {
    alert('Bulk owner assignment requires an assignment API. Export or edit selected clients individually for now.');
  };

  const handleBulkDownloadDocuments = () => {
    const files = selectedClients.flatMap(getClientFiles);
    if (!files.length) {
      alert('No documents are available for the selected clients.');
      return;
    }
    files.forEach((file) => window.open(`${API_BASE_URL}/${file.path}`, '_blank', 'noopener,noreferrer'));
  };

  const handleDelete = async () => {
    if (!canDeleteRetailClients) {
      alert('You do not have permission to delete retail clients.');
      setShowDeleteConfirmationModal(false);
      return;
    }

    if (confirmationId !== selectedClient.autoGeneratedId) {
      alert('The ID does not match. Deletion canceled.');
      setShowDeleteConfirmationModal(false);
      return;
    }

    try {
      await api.delete(`${API_BASE_URL}/api/client/${selectedClient.autoGeneratedId}`);
      alert('Client deleted successfully!');
      setClients(clients.filter(client => client.autoGeneratedId !== selectedClient.autoGeneratedId));
      setShowDeleteConfirmationModal(false);
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Error deleting client.');
      setShowDeleteConfirmationModal(false);
    }
  };

  const handleUpdate = async () => {
    if (!canEditRetailClients) {
      alert('You do not have permission to edit retail clients.');
      handleCloseModal();
      return;
    }

    const formDataToSubmit = new FormData();
    let hasChanges = false;

    for (const key in selectedClient) {
      if (typeof selectedClient[key] === 'function') continue;
      if (['_id', '__v', 'createdAt', 'updatedAt', 'ownerHistory', 'isProspect', 'autoGeneratedId'].includes(key)) {
        continue;
      }

      if (key === 'ownerId') {
        const currentOwnerId = getOwnerValue(selectedClient.ownerId);
        const initialOwnerId = getOwnerValue(initialClientState.current.ownerId);
        if (currentOwnerId !== initialOwnerId) {
          formDataToSubmit.append('ownerId', currentOwnerId);
          hasChanges = true;
        }
      } else if (key === 'aadharFile' || key === 'panFile') {
        if (selectedClient[key] instanceof File) {
          formDataToSubmit.append(key, selectedClient[key]);
          hasChanges = true;
        }
        // If it's a string (existing file path), don't re-append it to FormData
        // If you need to explicitly remove a file, a separate mechanism would be needed.
      } else if (key === 'familyMembers' || key === 'children') {
        // Always append familyMembers and children as JSON strings
        // Compare to detect changes for the 'No changes detected' alert
        if (!areArraysEqual(selectedClient[key], initialClientState.current[key])) {
          hasChanges = true;
        }
        formDataToSubmit.append(key, JSON.stringify(selectedClient[key]));
      } else {
        // Handle fields that might be null/undefined initially from backend but become strings after user input
        const currentValue = selectedClient[key] === null || selectedClient[key] === undefined ? '' : selectedClient[key];
        const initialValue = initialClientState.current[key] === null || initialClientState.current[key] === undefined ? '' : initialClientState.current[key];

        if (currentValue !== initialValue) {
          formDataToSubmit.append(key, currentValue);
          hasChanges = true;
        } else {
          // If a field hasn't changed, still append it to ensure the backend receives all expected data
          formDataToSubmit.append(key, currentValue);
        }
      }
    }

    if (!hasChanges && !(selectedClient.aadharFile instanceof File) && !(selectedClient.panFile instanceof File)) {
      alert('No changes detected to save.');
      handleCloseModal();
      return;
    }

    try {
      const response = await api.put(`${API_BASE_URL}/api/client/${selectedClient.autoGeneratedId}`, formDataToSubmit, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Client updated successfully!');
      setClients(
        clients.map(client =>
          client.autoGeneratedId === selectedClient.autoGeneratedId ? response.data : client
        )
      );
      handleCloseModal();
    } catch (error) {
      console.error('Error updating client:', error);
      alert('Error updating client.');
    }
  };

  const handleOpenDeleteConfirmationModal = (client) => {
    if (!canDeleteRetailClients) {
      alert('You do not have permission to delete retail clients.');
      return;
    }

    setSelectedClient(client);
    setShowDeleteConfirmationModal(true);
  };

  const handleCloseDeleteConfirmationModal = () => {
    setShowDeleteConfirmationModal(false);
    setConfirmationId('');
  };

  // Helper function to handle family member input changes
  const handleFamilyMemberChange = (index, field, value) => {
    const updatedFamilyMembers = [...selectedClient.familyMembers];
    updatedFamilyMembers[index][field] = value;
    setSelectedClient({ ...selectedClient, familyMembers: updatedFamilyMembers });
  };

  // Helper function to handle children input changes
  const handleChildrenMemberChange = (index, field, value) => {
    const updatedChildren = [...selectedClient.children];
    updatedChildren[index][field] = value;
    setSelectedClient({ ...selectedClient, children: updatedChildren });
  };

  // Function to add a new family member
  const handleAddFamilyMember = () => {
    const newFamilyMember = {
      relation: '', // You might want to pre-fill this or let the user choose
      name: '',
      phoneNumber: '',
      emailId: '',
      DOB: '',
      gender: '',
      occupation: '',
      aadharNumber: '',
      PANNumber: ''
    };
    setSelectedClient(prevClient => ({
      ...prevClient,
      familyMembers: [...prevClient.familyMembers, newFamilyMember]
    }));
  };

  // Function to add a new child
  const handleAddChild = () => {
    const newChild = {
      name: '',
      phoneNumber: '',
      emailId: '',
      DOB: '',
      gender: '',
      occupation: '',
      aadharNumber: '',
      PANNumber: ''
    };
    setSelectedClient(prevClient => ({
      ...prevClient,
      children: [...prevClient.children, newChild]
    }));
  };

  // Handle file input changes for Aadhar and PAN
  const handleFileChange = (e, fileType) => {
    setSelectedClient({ ...selectedClient, [fileType]: e.target.files[0] });
  };

  if (!isAuthenticated()) {
    return <Navigate to="/" />;
  }

  const isReadOnly = modalMode === 'view';

  return (
    <div className="client-list-container">
      <div className='sidebar'>
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <div className={`client-list-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="client-list-sticky-header">
          <Header />
        </div>
        <div className="client-list-scroll-area">
        <section className='client-list-section'>
          <div className="client-page-hero">
            <div>
              <span className="client-page-eyebrow">System Settings / Client Management</span>
              <h1>Retail Clients</h1>
              <p>Manage, view, and maintain all retail client records from one place.</p>
            </div>
            <div className="client-hero-stats">
              <span>Total Clients</span>
              <strong>{summary.total}</strong>
              <small>{summary.active} active · {summary.addedThisMonth} recently added</small>
            </div>
          </div>

          <div className="client-summary-grid">
            {summaryCards.map(({ label, value, note, icon: Icon, tone }) => (
              <article className={`client-summary-card ${tone}`} key={label}>
                <Icon size={22} />
                <span>{label}</span>
                <strong>{value}</strong>
                <small>{note}</small>
              </article>
            ))}
          </div>

          <section className="client-filter-card">
            <div className="client-filter-header">
              <div>
                <h2>Search & Filters</h2>
                <p>Search by client ID, name, mobile number, or email.</p>
              </div>
              <div className="client-result-pill">{filteredClients.length} records</div>
            </div>
            <div className="client-filter-grid">
              <label className="client-search-field">
                <span>Search</span>
                <div>
                  <Search size={17} />
                  <input
                    type="search"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    placeholder="Client ID, name, mobile, email"
                  />
                </div>
              </label>
              <label>
                <span>Owner</span>
                <select value={filters.owner} onChange={(e) => handleFilterChange('owner', e.target.value)}>
                  <option value="">All owners</option>
                  {filterOptions.owners.map((owner) => <option key={owner} value={owner}>{owner}</option>)}
                </select>
              </label>
              <label>
                <span>Assigned User</span>
                <select
                  value={filters.assignedUser}
                  onChange={(e) => handleFilterChange('assignedUser', e.target.value)}
                >
                  <option value="">All users</option>
                  {filterOptions.assignedUsers.map((user) => <option key={user} value={user}>{user}</option>)}
                </select>
              </label>
              <label>
                <span>City</span>
                <select value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)}>
                  <option value="">All cities</option>
                  {filterOptions.cities.map((city) => <option key={city} value={city}>{city}</option>)}
                </select>
              </label>
              <label>
                <span>State</span>
                <select value={filters.state} onChange={(e) => handleFilterChange('state', e.target.value)}>
                  <option value="">All states</option>
                  {filterOptions.states.map((state) => <option key={state} value={state}>{state}</option>)}
                </select>
              </label>
              <label>
                <span>Status</span>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
                  <option value="">All status</option>
                  {filterOptions.statuses.map((status) => <option key={status} value={status}>{status}</option>)}
                </select>
              </label>
            </div>
            <div className="client-action-row">
              <button type="button" className="client-btn client-btn-primary" onClick={handleSearch}>
                <Search size={16} /> Search
              </button>
              <button type="button" className="client-btn client-btn-ghost" onClick={handleResetFilters}>
                <RefreshCw size={16} /> Reset
              </button>
              {canExportRetailClients && (
                <button type="button" className="client-btn client-btn-success" onClick={() => exportClients()}>
                  <Download size={16} /> Export Excel
                </button>
              )}
              {canAddRetailClients && (
                <button type="button" className="client-btn client-btn-dark" onClick={() => navigate('/retail/clients-form')}>
                  <Plus size={16} /> Add Client
                </button>
              )}
            </div>
          </section>

          {selectedClientIds.length > 0 && (
            <div className="client-bulk-bar">
              <strong>{selectedClientIds.length} selected</strong>
              <div>
                <button type="button" onClick={handleBulkAssignOwner}><UserCheck size={15} /> Bulk Assign Owner</button>
                {canExportRetailClients && <button type="button" onClick={() => exportClients(selectedClients, 'selected-retail-clients.xlsx')}><Download size={15} /> Bulk Export</button>}
                <button type="button" onClick={handleBulkDownloadDocuments}><FileText size={15} /> Bulk Download Documents</button>
                {canDeleteRetailClients && <button type="button" className="is-danger" onClick={handleBulkDelete}><Trash2 size={15} /> Bulk Delete</button>}
              </div>
            </div>
          )}

          {loading ? (
            <div className="client-list-empty">Loading clients...</div>
          ) : filteredClients.length === 0 ? (
            <div className="client-empty-state">
              <Users size={44} />
              <h3>No Clients Found</h3>
              <p>Try changing the filters or add a new retail client record.</p>
              {canAddRetailClients && (
                <button type="button" className="client-btn client-btn-primary" onClick={() => navigate('/retail/clients-form')}>
                  <Plus size={16} /> Add Client
                </button>
              )}
            </div>
          ) : (
            <div className="client-table-card">
              <div className="client-table-toolbar">
                <div>
                  <h2>Retail Client Records</h2>
                </div>
              </div>
              <div className="client-table-scroll">
                <table className="client-table">
                  <thead>
                    <tr>
                      <th className="client-sticky-check">
                        <input
                          type="checkbox"
                          checked={selectedClientIds.length === filteredClients.length && filteredClients.length > 0}
                          onChange={(e) => toggleAllFilteredClients(e.target.checked)}
                          aria-label="Select all clients"
                        />
                      </th>
                      <th className="client-sticky-name">Client Name</th>
                      <th>Owner</th>
                      <th>Address</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Status</th>
                      <th>Files</th>
                      {canShowRetailActions && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => {
                      const files = getClientFiles(client);
                      const isSelected = selectedClientIds.includes(client.autoGeneratedId);
                      return (
                        <tr key={client.autoGeneratedId} className={isSelected ? 'is-selected' : ''}>
                          <td className="client-sticky-check" data-label="Select">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleClientSelection(client.autoGeneratedId)}
                              aria-label={`Select ${client.clientName}`}
                            />
                          </td>
                          <td className="client-sticky-name" data-label="Client Name">
                            <button
                              type="button"
                              className="client-name-cell"
                              onClick={canViewRetailClients ? () => setQuickViewClient(client) : undefined}
                              disabled={!canViewRetailClients}
                            >
                              <span className="client-avatar">{getInitials(client.clientName)}</span>
                              <span>
                                <strong>{client.clientName || '-'}</strong>
                                <small>Client ID: {client.autoGeneratedId || '-'}</small>
                              </span>
                            </button>
                          </td>
                          <td data-label="Owner">
                            <div className="client-owner-cell">
                              <span>{getInitials(getOwnerLabel(client))}</span>
                              <strong>{getOwnerLabel(client)}</strong>
                            </div>
                          </td>
                          <td data-label="Address">
                            <button
                              type="button"
                              className="client-address-preview"
                              title={client.prospectAddress || 'No address available'}
                              onClick={canViewRetailClients ? () => setQuickViewClient(client) : undefined}
                              disabled={!canViewRetailClients}
                            >
                              {client.prospectAddress || '-'}
                            </button>
                          </td>
                          <td data-label="Phone">
                            {client.phoneNumber ? (
                              <a className="client-contact-link" href={`tel:${client.phoneNumber}`}>
                                <Phone size={15} /> {client.phoneNumber}
                              </a>
                            ) : '-'}
                          </td>
                          <td data-label="Email">
                            {client.emailId ? (
                              <a className="client-contact-link" href={`mailto:${client.emailId}`}>
                                <Mail size={15} /> {client.emailId}
                              </a>
                            ) : '-'}
                          </td>
                          <td data-label="City">{client.city || '-'}</td>
                          <td data-label="State">{client.state || '-'}</td>
                          <td data-label="Status"><span className="client-status-badge">{getClientStatus(client)}</span></td>
                          <td data-label="Files">
                            <button
                              type="button"
                              className="client-files-pill"
                              onClick={canViewRetailClients ? () => setDocumentClient(client) : undefined}
                              disabled={!canViewRetailClients || files.length === 0}
                            >
                              <FileText size={15} /> {files.length} {files.length === 1 ? 'File' : 'Files'}
                            </button>
                          </td>
                          {canShowRetailActions && (
                            <td data-label="Actions">
                              <div className="client-action-group">
                                {canViewRetailClients && (
                                  <button
                                    type="button"
                                    className="client-action-icon-btn client-action-icon-btn-view"
                                    onClick={() => setQuickViewClient(client)}
                                    aria-label={`View ${client.clientName}`}
                                    title="View Client"
                                  >
                                    <Eye size={16} strokeWidth={2} />
                                  </button>
                                )}
                                {canEditRetailClients && (
                                  <button
                                    type="button"
                                    className="client-action-icon-btn client-action-icon-btn-edit"
                                    onClick={() => openClientModal(client, 'edit')}
                                    aria-label={`Edit ${client.clientName}`}
                                    title="Edit Client"
                                  >
                                    <Pencil size={16} strokeWidth={2} />
                                  </button>
                                )}
                                {canDeleteRetailClients && (
                                  <button
                                    type="button"
                                    className="client-action-icon-btn client-action-icon-btn-delete"
                                    onClick={() => handleOpenDeleteConfirmationModal(client)}
                                    aria-label={`Delete ${client.clientName}`}
                                    title="Delete Client"
                                  >
                                    <Trash2 size={16} strokeWidth={2} />
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {quickViewClient && (
            <div className="client-drawer-overlay" onClick={() => setQuickViewClient(null)}>
              <aside className="client-quick-drawer" onClick={(e) => e.stopPropagation()}>
                <div className="client-drawer-head">
                  <div className="client-drawer-title">
                    <span className="client-avatar client-avatar-lg">{getInitials(quickViewClient.clientName)}</span>
                    <div>
                      <h3>{quickViewClient.clientName || 'Retail Client'}</h3>
                      <p>Client ID: {quickViewClient.autoGeneratedId || '-'}</p>
                    </div>
                  </div>
                  <button type="button" className="client-drawer-close" onClick={() => setQuickViewClient(null)} aria-label="Close quick view">
                    <X size={18} />
                  </button>
                </div>
                <div className="client-drawer-body">
                  <section>
                    <h4>Client Details</h4>
                    <div className="client-detail-grid">
                      <span>Status</span><strong>{getClientStatus(quickViewClient)}</strong>
                      <span>Date of Birth</span><strong>{quickViewClient.prospectDOB || '-'}</strong>
                      <span>Gender</span><strong>{quickViewClient.prospectGender || '-'}</strong>
                      <span>Occupation</span><strong>{quickViewClient.prospectOccupation || '-'}</strong>
                    </div>
                  </section>
                  <section>
                    <h4>Contact Details</h4>
                    <div className="client-drawer-contact">
                      <a href={quickViewClient.phoneNumber ? `tel:${quickViewClient.phoneNumber}` : undefined}>
                        <Phone size={16} /> {quickViewClient.phoneNumber || '-'}
                      </a>
                      <a href={quickViewClient.emailId ? `mailto:${quickViewClient.emailId}` : undefined}>
                        <Mail size={16} /> {quickViewClient.emailId || '-'}
                      </a>
                      <p><MapPin size={16} /> {quickViewClient.prospectAddress || '-'}</p>
                    </div>
                  </section>
                  <section>
                    <h4>Policies</h4>
                    <p className="client-drawer-muted">
                      {hasClientPolicy(quickViewClient) ? 'Policy information is linked to this client.' : 'No linked policy found.'}
                    </p>
                  </section>
                  <section>
                    <h4>Documents</h4>
                    <div className="client-document-list">
                      {getClientFiles(quickViewClient).length ? (
                        getClientFiles(quickViewClient).map((file) => (
                          <a key={file.label} href={`${API_BASE_URL}/${file.path}`} target="_blank" rel="noreferrer">
                            <FileText size={16} /> {file.label}
                          </a>
                        ))
                      ) : (
                        <p className="client-drawer-muted">No documents uploaded.</p>
                      )}
                    </div>
                  </section>
                  <section>
                    <h4>Assigned User</h4>
                    <div className="client-owner-cell client-owner-cell-large">
                      <span>{getInitials(getAssignedUserLabel(quickViewClient))}</span>
                      <strong>{getAssignedUserLabel(quickViewClient)}</strong>
                    </div>
                  </section>
                  <section>
                    <h4>Notes</h4>
                    <p className="client-drawer-muted">{quickViewClient.notes || quickViewClient.remarks || 'No notes available.'}</p>
                  </section>
                </div>
              </aside>
            </div>
          )}

          {documentClient && (
            <div className="client-data-modal-overlay">
              <div className="client-document-modal">
                <button className="close-btn" onClick={() => setDocumentClient(null)}>X</button>
                <h3>Client Documents</h3>
                <p>{documentClient.clientName} · {getClientFiles(documentClient).length} file(s)</p>
                <div className="client-document-list">
                  {getClientFiles(documentClient).length ? (
                    getClientFiles(documentClient).map((file) => (
                      <a key={file.label} href={`${API_BASE_URL}/${file.path}`} target="_blank" rel="noreferrer">
                        <FileText size={16} /> View {file.label}
                      </a>
                    ))
                  ) : (
                    <span>No files uploaded for this client.</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Modal to view/edit client details */}
          {showModal && selectedClient && (
            <div className="client-data-modal-overlay">
              <div className="client-edit-modal-content client-edit-modal">
                <button className="close-btn" onClick={handleCloseModal}>X</button>
                <h3>{isReadOnly ? 'View Client Details' : 'Edit Client Details'}</h3>
                <div className="client-edit-summary">
                  <span className="client-avatar client-avatar-lg">{getInitials(selectedClient.clientName)}</span>
                  <div>
                    <strong>{selectedClient.clientName || 'Retail Client'}</strong>
                    <small>Client ID: {selectedClient.autoGeneratedId || '-'}</small>
                  </div>
                  <div>
                    <span>{getClientStatus(selectedClient)}</span>
                    <small>{getOwnerLabel(selectedClient) !== '-' ? `Owner: ${getOwnerLabel(selectedClient)}` : 'Owner not assigned'}</small>
                  </div>
                </div>
                <form className="client-edit-form">
                  <div className="client-edit-section-title">
                    <h4>Basic Details</h4>
                    <p>Core client identity and ownership information.</p>
                  </div>
                  {/* Prospect Name */}
                  <label>Name:</label>
                  <input
                    type="text"
                    value={selectedClient.clientName || ''} // Added || '' for safety
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, clientName: e.target.value })}
                  />

                  {canReassignRetailOwner && (
                    <>
                      <label>Owner:</label>
                      <select
                        value={getOwnerValue(selectedClient.ownerId)}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          setSelectedClient({ ...selectedClient, ownerId: e.target.value })
                        }
                      >
                        <option value="">Select owner</option>
                        {employeeOptions.map((employee) => (
                          <option key={employee._id} value={employee._id}>
                            {employee.name}
                            {employee.employeeId ? ` (${employee.employeeId})` : ''}
                          </option>
                        ))}
                      </select>

                      <div className="owner-history-panel client-edit-full-width">
                        <h4>Reassignment Log</h4>
                        {selectedClient.ownerHistory?.length ? (
                          <div className="owner-history-list">
                            {selectedClient.ownerHistory
                              .slice()
                              .reverse()
                              .map((entry, index) => (
                                <div className="owner-history-item" key={`${entry.changedAt}-${index}`}>
                                  <strong>{entry.fromOwnerName}</strong> to <strong>{entry.toOwnerName}</strong>
                                  <span>
                                    {entry.changedByName} on {formatAuditTime(entry.changedAt)}
                                  </span>
                                </div>
                              ))}
                          </div>
                        ) : (
                          <p className="owner-history-empty">No reassignment log available.</p>
                        )}
                      </div>
                    </>
                  )}

                  <div className="client-edit-section-title">
                    <h4>Contact & Location</h4>
                    <p>Primary contact channels and address details.</p>
                  </div>

                  {/* Phone Number */}
                  <label>Phone:</label>
                  <input
                    type="text"
                    value={selectedClient.phoneNumber || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, phoneNumber: e.target.value })}
                  />

                  {/* Email ID */}
                  <label>Email:</label>
                  <input
                    type="email"
                    value={selectedClient.emailId || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, emailId: e.target.value })}
                  />

                  {/* Prospect Address */}
                  <label>Address:</label>
                  <textarea
                    rows="3"
                    value={selectedClient.prospectAddress || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, prospectAddress: e.target.value })}
                  />

                  {/* PIN code */}
                  <label>PIN Code:</label>
                  <input
                    type="text"
                    value={selectedClient.pinCode || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, pinCode: e.target.value })}
                  />

                  {/* State */}
                  <label>State:</label>
                  <input
                    type="text"
                    value={selectedClient.state || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, state: e.target.value })}
                  />

                  {/* City */}
                  <label>City:</label>
                  <input
                    type="text"
                    value={selectedClient.city || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, city: e.target.value })}
                  />

                  <div className="client-edit-section-title">
                    <h4>Personal Details</h4>
                    <p>Demographic and income profile used for retail policy servicing.</p>
                  </div>

                  {/* DOB */}
                  <label>Date of Birth:</label>
                  <input
                    type="date"
                    value={selectedClient.prospectDOB || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, prospectDOB: e.target.value })}
                  />

                  {/* Gender */}
                  <label>Gender:</label>
                  <select
                    value={selectedClient.prospectGender || ''}
                    disabled={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, prospectGender: e.target.value })}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>

                  {/* Marital Status */}
                  <label>Marital Status:</label>
                  <select
                    value={selectedClient.maritalStatus || ''}
                    disabled={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, maritalStatus: e.target.value })}
                  >
                    <option value="">Select Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                  </select>

                  {/* Prospect Occupation */}
                  <label>Prospect Occupation:</label>
                  <select
                    value={selectedClient.prospectOccupation || ''}
                    disabled={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, prospectOccupation: e.target.value })}
                  >
                    <option value="">Select Occupation</option>
                    <option value="Software Developer">Software Developer</option>
                    <option value="Sales">Sales</option>
                    <option value="Business">Business</option>
                  </select>

                  {/* Yearly Income */}
                  <label>Yearly Income:</label>
                  <input
                    type="text"
                    value={selectedClient.incomeYearly || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, incomeYearly: e.target.value })}
                  />

                  {/* Aadhar Number */}
                  <label>Aadhar Number:</label>
                  <input
                    type="text"
                    value={selectedClient.aadharNumber || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, aadharNumber: e.target.value })}
                  />

                  {/* PAN Number */}
                  <label>PAN Number:</label>
                  <input
                    type="text"
                    value={selectedClient.panNumber || ''}
                    readOnly={isReadOnly}
                    onChange={(e) => setSelectedClient({ ...selectedClient, panNumber: e.target.value })}
                  />

                  <div className="client-edit-section-title">
                    <h4>Documents</h4>
                    <p>Upload or review identity documents linked to this client.</p>
                  </div>

                  {/* Aadhar File */}
                  <label>Aadhar File:</label>
                  {!isReadOnly && (
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, 'aadharFile')}
                    />
                  )}
                  {selectedClient.aadharFile && typeof selectedClient.aadharFile === 'string' && (
                    <p className="client-file-note">Current Aadhar File: <a href={`${API_BASE_URL}/${selectedClient.aadharFile}`} target="_blank" rel="noreferrer">View Current</a></p>
                  )}


                  {/* PAN File */}
                  <label>PAN File:</label>
                  {!isReadOnly && (
                    <input
                      type="file"
                      onChange={(e) => handleFileChange(e, 'panFile')}
                    />
                  )}
                  {selectedClient.panFile && typeof selectedClient.panFile === 'string' && (
                    <p className="client-file-note">Current PAN File: <a href={`${API_BASE_URL}/${selectedClient.panFile}`} target="_blank" rel="noreferrer">View Current</a></p>
                  )}

                  {/* Family Members */}
                  <div className="member-section-heading"><h2>Family Members</h2></div>
                  {selectedClient.familyMembers && selectedClient.familyMembers.length > 0 ? (
                    selectedClient.familyMembers.map((member, index) => (
                      <div key={index} className="family-member-form">
                        <h4>{member.relation || `Member ${index + 1}`}</h4> {/* Display relation or generic name */}
                        <label>Relation:</label>
                        <input
                          type="text"
                          value={member.relation || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'relation', e.target.value)}
                        />
                        <label>Name:</label>
                        <input
                          type="text"
                          value={member.name || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'name', e.target.value)}
                        />

                        <label>Phone:</label>
                        <input
                          type="text"
                          value={member.phoneNumber || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'phoneNumber', e.target.value)}
                        />

                        <label>Email:</label>
                        <input
                          type="email"
                          value={member.emailId || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'emailId', e.target.value)}
                        />

                        <label>DOB:</label>
                        <input
                          type="date"
                          value={member.DOB || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'DOB', e.target.value)}
                        />

                        <label>Gender:</label>
                        <select
                          value={member.gender || ''}
                          disabled={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'gender', e.target.value)}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>

                        <label> Occupation:</label>
                        <input
                          type="text"
                          value={member.occupation || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'occupation', e.target.value)}
                        />

                        <label>Aadhar Number:</label>
                        <input
                          type="text"
                          value={member.aadharNumber || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'aadharNumber', e.target.value)}
                        />

                        <label>PAN Number:</label>
                        <input
                          type="text"
                          value={member.PANNumber || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleFamilyMemberChange(index, 'PANNumber', e.target.value)}
                        />
                      </div>
                    ))
                  ) : (
                    <p className="member-empty-state">No family members added yet.</p>
                  )}
                  {!isReadOnly && (
                    <button type="button" className="member-add-btn" onClick={handleAddFamilyMember}>Add Family Member</button>
                  )}

                  {/* Childrens */}
                  <div className="member-section-heading"><h2>Children</h2></div>
                  {selectedClient.children && selectedClient.children.length > 0 ? (
                    selectedClient.children.map((member, index) => (
                      <div key={index} className="family-member-form"> {/* Reusing class, consider renaming if styles differ */}
                        <h4>Child {index + 1}</h4>
                        <label>Name:</label>
                        <input
                          type="text"
                          value={member.name || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'name', e.target.value)}
                        />

                        <label>Phone:</label>
                        <input
                          type="text"
                          value={member.phoneNumber || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'phoneNumber', e.target.value)}
                        />

                        <label>Email:</label>
                        <input
                          type="email"
                          value={member.emailId || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'emailId', e.target.value)}
                        />

                        <label>DOB:</label>
                        <input
                          type="date"
                          value={member.DOB || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'DOB', e.target.value)}
                        />

                        <label>Gender:</label>
                        <select
                          value={member.gender || ''}
                          disabled={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'gender', e.target.value)}
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>

                        <label> Occupation:</label>
                        <input
                          type="text"
                          value={member.occupation || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'occupation', e.target.value)}
                        />

                        <label>Aadhar Number:</label>
                        <input
                          type="text"
                          value={member.aadharNumber || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'aadharNumber', e.target.value)}
                        />

                        <label>PAN Number:</label>
                        <input
                          type="text"
                          value={member.PANNumber || ''}
                          readOnly={isReadOnly}
                          onChange={(e) => handleChildrenMemberChange(index, 'PANNumber', e.target.value)}
                        />
                      </div>
                    ))
                  ) : (
                    <p className="member-empty-state">No children added yet.</p>
                  )}
                  {!isReadOnly && (
                    <button type="button" className="member-add-btn" onClick={handleAddChild}>Add Child</button>
                  )}

                  {/* Save Changes Button */}
                  <div className="client-data-modal-actions">
                    {!isReadOnly && (
                      <button type="button" onClick={handleUpdate}>Save Changes</button>
                    )}
                    <button type="button" onClick={handleCloseModal}>
                      {isReadOnly ? 'Close' : 'Cancel'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}




          {/* Delete Confirmation Modal */}
          {showDeleteConfirmationModal && (
            <div className="confirmation-modal-overlay">
              <div className="confirmation-modal-content">
                <button className="close-btn" onClick={handleCloseDeleteConfirmationModal}>X</button>
                <h3>Confirm Deletion</h3>
                <p>Please type the client ID to confirm deletion:</p>
                <input
                  type="text"
                  value={confirmationId}
                  onChange={(e) => setConfirmationId(e.target.value)}
                  placeholder="Enter client ID"
                />
                <div className="confirmation-modal-actions">
                  <button className="confirm-btn" onClick={handleDelete}>Confirm Deletion</button>
                  <button className="cancel-btn" onClick={handleCloseDeleteConfirmationModal}>Cancel</button>
                </div>
              </div>
            </div>
          )}
        </section>
        </div>
      </div>
    </div>
  );
};

export default RetailClientList;
